import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import clsx from 'clsx';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import ChevronDown from '@/assets/icons/chevron-down.svg';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import { queryKeys } from '@/query-keys';

import { ChannelSearchDropdown } from './channel-search-dropdown';

const CHANNEL_SEARCH_DEBOUNCE_MS = 300;

interface ChannelSearchSelectProps {
  selectedChannelId?: number;
  selectedChannelName?: string;
  onChannelChange: (channelId: number | undefined, name?: string) => void;
}

export function ChannelSearchSelect({
  selectedChannelId,
  selectedChannelName,
  onChannelChange,
}: ChannelSearchSelectProps) {
  const intl = useIntl();
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [popoverWidth, setPopoverWidth] = useState<number>();
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearchQuery(inputValue),
      CHANNEL_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (selectedChannelId != null && selectedChannelName) {
      setInputValue(selectedChannelName);
    }
  }, [selectedChannelId, selectedChannelName]);

  const trimmedSearch = debouncedSearchQuery.trim();
  const shouldFetch = open && trimmedSearch.length > 0;
  const { data, isFetching } = useQuery({
    queryKey: [...queryKeys.settings.contracts.vonqChannels, trimmedSearch],
    queryFn: () =>
      SourcingService.getInternalApiSourcingVonqContractsChannels(
        trimmedSearch,
      ),
    enabled: shouldFetch,
  });

  const channelOptions = useMemo(() => {
    if (!shouldFetch) return [];

    return (data?.channels ?? []).map(channel => ({
      id: channel.id,
      name: channel.name,
      logoSquareUrl: channel.logo_square_url,
    }));
  }, [data?.channels, shouldFetch]);

  const hasSearchQuery = inputValue.trim().length > 0;
  const isDebouncingSearch =
    hasSearchQuery && inputValue !== debouncedSearchQuery;
  const isFetchingFromApi = shouldFetch && isFetching;

  const handleOpenDropdown = () => {
    setOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOpen(true);
    setInputValue(e.target.value);
    onChannelChange(undefined, undefined);
  };

  const handleSelectChannel = (channelId: number, name: string) => {
    setInputValue(name);
    onChannelChange(channelId, name);
    setOpen(false);
    inputRef.current?.blur();
  };

  useEffect(() => {
    if (open && anchorRef.current) {
      setPopoverWidth(anchorRef.current.offsetWidth);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="relative w-full">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleOpenDropdown}
            placeholder={intl.formatMessage({
              defaultMessage: 'Type a contract name',
              description:
                'Placeholder for the job channel search input in add contract sheet',
            })}
            className={clsx(
              'placeholder:text-fx-neutral-600 text-fx-neutral-900 h-10 w-full rounded-sm border bg-white py-2 pr-8 pl-2 text-sm transition-[color,box-shadow] outline-none',
              'border-fx-neutral-300',
              'focus:border-fx-brand-600 focus:shadow-selected',
              open && 'border-fx-brand-600 shadow-selected',
            )}
            aria-autocomplete="list"
            aria-controls={open ? listboxId : undefined}
          />
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            className="text-fx-neutral-400 hover:text-fx-neutral-600 pointer-events-auto absolute inset-y-0 right-0 flex w-8 items-center justify-center"
            onClick={e => {
              e.preventDefault();
              handleOpenDropdown();
              inputRef.current?.focus();
            }}
          >
            <img src={ChevronDown} alt="" className="h-4 w-4" />
          </button>
        </div>
      </PopoverAnchor>
      {open && (
        <PopoverContent
          className="border-fx-neutral-200 pointer-events-auto z-50 overflow-hidden rounded-sm border bg-white !p-1 shadow-lg outline-none"
          align="start"
          sideOffset={2}
          style={popoverWidth ? { width: `${popoverWidth}px` } : undefined}
          onOpenAutoFocus={e => e.preventDefault()}
          onInteractOutside={e => {
            if (inputRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          <div className="max-h-[280px] overflow-y-auto leading-none">
            <ChannelSearchDropdown
              isDebouncing={isDebouncingSearch}
              isFetching={isFetchingFromApi}
              hasSearchQuery={hasSearchQuery}
              options={channelOptions}
              selectedChannelId={selectedChannelId}
              onSelect={handleSelectChannel}
              listboxId={listboxId}
            />
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}
